import { Judgment, TTSType, ApiError } from '../types';
import { debugLog } from '../utils/logger';

// API endpoint - in production, we'll use relative URLs since the backend serves the frontend
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // In production, use relative URL
  : 'http://localhost:3001/api'; // In development, use localhost

// Store the current game difficulty
let currentDifficulty = 'easy';

// Function to set the game difficulty
export const setGameDifficulty = (difficulty: string): void => {
    debugLog('Setting game difficulty', { difficulty });
    currentDifficulty = difficulty;
};

// API service functions
// Helper function to handle API errors, including rate limiting
const handleApiError = async (response: Response, functionName: string): Promise<ApiError> => {
    let errorMessage = `Failed with status: ${response.status}`;
    let isRateLimit = false;

    try {
        // Try to parse the error response as JSON
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
        debugLog(`${functionName} API error details`, errorData);
    } catch (e) {
        // If parsing fails, use the status text
        errorMessage = response.statusText || errorMessage;
    }

    // Check if this is a rate limit error
    if (response.status === 429) {
        isRateLimit = true;
        errorMessage = "Rate limit exceeded. Please try again later.";
    }

    return { message: errorMessage, isRateLimit, status: response.status };
};

export const generateInsult = async (): Promise<string> => {
    debugLog('Generating insult - API call initiated', { difficulty: currentDifficulty });
    try {
        const response = await fetch(`${API_URL}/generate-insult`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ difficulty: currentDifficulty }),
        });
        
        if (!response.ok) {
            const error = await handleApiError(response, 'generateInsult');
            throw error;
        }
        
        const data = await response.json();
        debugLog('Successfully parsed insult response', { insult: data.insult });
        return data.insult;
    } catch (err) {
        const isApiError = err && typeof err === 'object' && 'isRateLimit' in err;
        
        if (isApiError) {
            // Rethrow API errors so they can be handled by the UI
            throw err;
        }
        
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        debugLog('Error in generateInsult', { error: errorMessage });
        return "Ye fight like a dairy farmer, ye scurvy bilge rat!";
    }
};

export const generateComeback = async (insult: string): Promise<string> => {
    debugLog('Generating comeback - API call initiated', { insult, difficulty: currentDifficulty });
    try {
        const response = await fetch(`${API_URL}/generate-comeback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ insult, difficulty: currentDifficulty }),
        });
        
        if (!response.ok) {
            const error = await handleApiError(response, 'generateComeback');
            throw error;
        }
        
        const data = await response.json();
        debugLog('Successfully parsed comeback response', { comeback: data.comeback });
        return data.comeback;
    } catch (err) {
        const isApiError = err && typeof err === 'object' && 'isRateLimit' in err;
        
        if (isApiError) {
            // Rethrow API errors so they can be handled by the UI
            throw err;
        }
        
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        debugLog('Error in generateComeback', { error: errorMessage });
        return "Arr, that be the best ye got? My parrot be more intimidatin'!";
    }
};

export const judgeInsultBattle = async (insult: string, comeback: string): Promise<Judgment> => {
    debugLog('Judging battle - API call initiated', { insult, comeback, difficulty: currentDifficulty });
    try {
        const response = await fetch(`${API_URL}/judge-battle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ insult, comeback, difficulty: currentDifficulty }),
        });
        
        if (!response.ok) {
            const error = await handleApiError(response, 'judgeInsultBattle');
            throw error;
        }
        
        const data = await response.json();
        debugLog('Successfully parsed judgment response', { winner: data.winner, explanation: data.explanation });
        return data;
    } catch (err) {
        const isApiError = err && typeof err === 'object' && 'isRateLimit' in err;
        
        if (isApiError) {
            // Rethrow API errors so they can be handled by the UI
            throw err;
        }
        
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        debugLog('Error in judgeInsultBattle', { error: errorMessage });
        return {
            winner: Math.random() > 0.5 ? 'insult' : 'comeback',
            explanation: "The judge be too drunk on grog to make a proper call!",
        };
    }
};

export const generateTTS = async (text: string, type: TTSType): Promise<Blob> => {
    debugLog('Generating TTS - API call initiated', { text, type });
    try {
        const response = await fetch(`${API_URL}/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, type }),
        });
        
        if (!response.ok) {
            const error = await handleApiError(response, 'generateTTS');
            throw error;
        }
        
        const audioBlob = await response.blob();
        debugLog('Successfully received TTS audio');
        return audioBlob;
    } catch (err) {
        const isApiError = err && typeof err === 'object' && 'isRateLimit' in err;
        
        if (isApiError) {
            // Rethrow API errors so they can be handled by the UI
            throw err;
        }
        
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        debugLog('Error in generateTTS', { error: errorMessage });
        throw new Error(`TTS generation failed: ${errorMessage}`);
    }
};
